import { Dispatch, SetStateAction, useContext, useState } from "react";
import { ISsrData } from "../ViewDetailModal";
import './style.scss'
import CustomButton from "../../../CustomButton/CustomButton";
import { raiseSpecialServiceRequest } from "../../../../api/quotation.api";
import SnackbarContext from "../../../../context/snackbar.context";

interface ISsrSection {
  quotationStatus: string;
  ssrData: ISsrData[];
  setSsrData: Dispatch<SetStateAction<any>>;
}

interface ISsrPayload {
  quotationItemId: string;
  specialRequestId: string[];
  requestRemarks: string;
}

const SsrSection: React.FC<ISsrSection> = ({ quotationStatus, ssrData, setSsrData }) => {
  const snackbarCtx = useContext(SnackbarContext);
  const [payloadObj, setPayloadObj] = useState<ISsrPayload>({ quotationItemId: '', specialRequestId: [], requestRemarks: '' });
  
  const handleRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setPayloadObj((prevPayload) => ({ ...prevPayload, requestRemarks: value }));
  };

  const handleSelectedSsr = (e: React.ChangeEvent<HTMLInputElement>, specialRequestId: string, quotationItemId: string) => {
    if (!e.target.checked && payloadObj.specialRequestId.length === 1)
      setPayloadObj({ quotationItemId: "", specialRequestId: [], requestRemarks: "" });
    else if (!e.target.checked)
      setPayloadObj({
        ...payloadObj,
        specialRequestId: payloadObj.specialRequestId.filter((ssrId: string) => ssrId !== specialRequestId),
      });
    else
      setPayloadObj({
        ...payloadObj,
        specialRequestId: [...payloadObj.specialRequestId, specialRequestId],
        quotationItemId: quotationItemId,
      });
  };

  const handleSsrRequest = async () => {
    const data = await raiseSpecialServiceRequest(payloadObj);
    if (data.status === 201) {
      snackbarCtx.toggleSnackbar({
        message: 'Special service request raised !',
        type: "SUCCESS",
        visible: true,
      });
      setSsrData((prevSsrData: ISsrData[]) => 
        prevSsrData.map((ssrItem: ISsrData) => {
          if (payloadObj.specialRequestId.includes(ssrItem.specialRequestId)) {
            return { ...ssrItem, status: 'REQUESTED_FOC' };
          }
          return ssrItem;
        })
      );      
      setPayloadObj({ quotationItemId: '', specialRequestId: [], requestRemarks: '' });
    } else {
      snackbarCtx.toggleSnackbar({
        message: data?.response?.data?.message,
        type: "FAILURE",
        visible: true,
      });
    }
  }

  return (
    <div className="ssr-section-wrapper flex gap-2 w-full h-full max-h-[40vh] overflow-auto">
      <div className="paid-ssr-section-wrapper border-1 rounded-md p-3 flex flex-col gap-4 w-1/2 h-full">
        <span className="text-xl font-medium text-[#3a3a3a]">Paid Special Services</span>
        <div className="paid-ssr-items-wrapper flex flex-col gap-2.5">
          {ssrData?.map((ssrItem, index) => {
            return (
              !!ssrItem?.price && (
                <div className="ssr-input-item-wrapper border-1 px-2.5 py-2" key={index}>
                  <input
                    type="checkbox"
                    checked={payloadObj?.specialRequestId?.includes(ssrItem?.specialRequestId)}
                    id={ssrItem?.specialRequestId}
                    onChange={(e) => handleSelectedSsr(e, ssrItem?.specialRequestId, ssrItem?.quotationItemId)}
                    disabled={ssrItem?.status !== "ADDED" || quotationStatus !== 'ACTIVE'}
                  />
                  <label htmlFor={ssrItem?.specialRequestId} className="flex gap-2 items-center w-100">
                    <div key={ssrItem?.specialRequestId} className="paid-ssr-item-wrapper flex items-center justify-between w-100">
                      <span className="text-sm font-normal text-[#3a3a3a]">{ssrItem?.name}</span>
                      {ssrItem?.status !== 'ADDED' && <span className="text-[10px] font-semibold bg-[#FFF3CD] px-1 border-1 border-[#FFEBAF] rounded-md">STATUS: { ssrItem?.status.replace(/^FOC_|_FOC$/g, "") }</span>}
                      <span className="text-sm font-normal text-[#3a3a3a]">₹{ssrItem?.price}</span>
                    </div>
                  </label>
                </div>
              )
            );
          })}
        </div>
      </div>
      <div className="remarks-ssr-section-wrapper border-1 rounded-md p-3 flex flex-col gap-3 w-1/2 h-full">
        <div className="flex flex-col gap-2">
          <span className="text-xl font-medium text-[#3a3a3a]">Special Service Selected: </span>
          <span className="text-base font-medium text-[#3a3a3a] whitespace-normal">
            {!ssrData.length ? "None" : !!payloadObj?.specialRequestId?.length
                ? ssrData.filter(item => payloadObj.specialRequestId.includes(item.specialRequestId)).map(item => item.name).join(', ') || "None"
                : "None"}
          </span>
        </div>
        <div className="flex flex-col gap-2 remarks-textarea-wrapper">
          <label htmlFor="remarks">Add Remarks</label>
          <textarea
            name="remarks"
            id="reamrks"
            className="w-100 min-h-[60px]"
            placeholder="Remarks"
            value={payloadObj?.requestRemarks}
            onChange={handleRemarksChange}
            disabled={!payloadObj?.specialRequestId || quotationStatus !== 'ACTIVE'}
          ></textarea>
        </div>
        <CustomButton
          clx="self-end mt-2"
          onButtonClick={handleSsrRequest}
          disabled={!payloadObj?.specialRequestId.length || !payloadObj?.requestRemarks?.trim() || quotationStatus !== 'ACTIVE'}
        >
          Raise Request
        </CustomButton>
      </div>
    </div>
  );
};

export default SsrSection;
